/**
 * Pinterest Advertising REST helpers (Story 19.4, Task 2)
 *
 * Helper functions for the Pinterest API v5 endpoints used during OAuth
 * post-callback validation:
 *
 *   - GET /v5/user_account                       — authenticated user profile + account_type
 *   - GET /v5/ad_accounts?page_size=100          — enumerate every ad account this OAuth grant can access
 *
 * Pinterest uses a single credential per request (Bearer access token); there
 * is no Developer-Token-style second credential like Microsoft Ads.
 *
 * @see https://developers.pinterest.com/docs/api/v5/user_account-get/
 * @see https://developers.pinterest.com/docs/api/v5/ad_accounts-list/
 */

import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { PinterestAdsApiCallError, parsePinterestAdsApiError } from "./errors";

// ============================================================================
// Constants
// ============================================================================

export const PINTEREST_API_BASE = "https://api.pinterest.com/v5";

const API_TIMEOUT_MS = 25_000;

/**
 * Defence-in-depth cap on cursor pagination. 20 pages × 100 items/page = 2,000
 * ad accounts hard limit per OAuth grant. Realistic agency holdcos almost
 * always sit well under 100; we expose `partialDiscovery=true` if the cap is
 * hit so 19-5's account-picker can warn the user about un-enumerated
 * sub-accounts.
 */
export const PINTEREST_AD_ACCOUNTS_MAX_PAGES = 20;

// ============================================================================
// Types
// ============================================================================

export type PinterestUserAccountType = "PINNER" | "BUSINESS" | "PARTNER";

export interface PinterestAdsUserAccount {
  /** Pinterest username (e.g., "@apex_digital") */
  username: string;
  /** Account class — only BUSINESS / PARTNER can hold ad accounts */
  accountType: PinterestUserAccountType;
  /** Profile image URL (best-effort, may be undefined) */
  profileImage?: string;
  /** Linked website URL (best-effort, may be undefined) */
  websiteUrl?: string;
}

/**
 * Subset of Pinterest's `AdAccount` schema we consume during OAuth onboarding.
 * The full record has many more fields; we only persist what the OAuth flow +
 * Story 19-5's account picker need.
 *
 * @see https://developers.pinterest.com/docs/api/v5/ad_accounts-list/
 */
export interface PinterestAdAccount {
  /** Numeric string ID, e.g. "549755819515" */
  id: string;
  /** Display name set by the advertiser */
  name: string;
  /** ISO 4217 currency code, e.g. "USD" / "EUR" */
  currency: string;
  /** ISO country code, e.g. "US" / "GB" */
  country: string;
  /** Owner user info (the Pinterest user who owns the account) */
  owner: {
    id: string;
    username: string;
  };
  /** Optional agency holding this account on behalf of an advertiser */
  agencyId?: string;
  /**
   * Business-Access roles the operation user holds on this ad account
   * (e.g. `["OWNER"]`, `["ADMIN", "ANALYST"]`) — the owned-vs-shared
   * signal (Story 31.13 AC #6). Pinterest marks the field readOnly and may
   * omit it; we OMIT the property when absent (the adapter normalises to
   * `[]` in `metadata.permissions`). Enum per the v5 OpenAPI spec
   * `BusinessAccessRole`: OWNER | ADMIN | ANALYST | SOS_READER |
   * FINANCE_MANAGER | FINANCE_VIEW | FINANCE_EDIT | AUDIENCE_MANAGER |
   * CAMPAIGN_MANAGER.
   */
  permissions?: string[];
}

export interface PinterestAdAccountsResult {
  /** Sorted ascending by numeric `id` for deterministic primary selection */
  accounts: PinterestAdAccount[];
  /**
   * True when MAX_PAGES was hit and there may be more ad accounts the OAuth
   * grant can access. Logged as a warning so 19-5 can surface a banner in the
   * account picker.
   */
  partialDiscovery: boolean;
}

// ============================================================================
// Header Builder
// ============================================================================

/**
 * Builds Pinterest API v5 REST headers.
 *
 * Pinterest only requires a single auth header (`Authorization: Bearer ...`).
 * Unlike Microsoft Ads there is no second universal credential (Developer Token).
 */
export function buildPinterestHeaders(
  accessToken: string
): Record<string, string> {
  if (!accessToken) {
    throw new Error("accessToken is required for Pinterest Ads API calls");
  }
  return {
    Authorization: `Bearer ${accessToken}`,
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

// ============================================================================
// Response shapes (raw Pinterest envelopes)
// ============================================================================

interface RawUserAccountResponse {
  username?: string;
  account_type?: string;
  profile_image?: string;
  website_url?: string;
}

interface RawAdAccountsResponse {
  items?: Array<{
    id?: string | number;
    name?: string;
    currency?: string;
    country?: string;
    owner?: {
      id?: string | number;
      username?: string;
    };
    agency_id?: string | number;
    /** Business-Access roles; readOnly, may be absent. Guarded as unknown — only string elements are kept. */
    permissions?: unknown;
  }>;
  /** Cursor for next page; absent/empty means no more pages */
  bookmark?: string;
}

// ============================================================================
// User Account
// ============================================================================

/**
 * Calls GET /v5/user_account and normalises the response into a stable shape.
 *
 * Returns the authenticated user's username + account_type. Used immediately
 * after token exchange to enrich structured logging — the actual ads-access
 * decision is made in `fetchPinterestAdsAdAccounts` (a user with `account_type
 * = "PINNER"` has no ad-account access and `/v5/ad_accounts` will return zero
 * items).
 */
export async function fetchPinterestAdsUserAccount(
  accessToken: string
): Promise<PinterestAdsUserAccount> {
  const headers = buildPinterestHeaders(accessToken);

  const result = await safeFetch(
    `${PINTEREST_API_BASE}/user_account`,
    { method: "GET", headers },
    API_TIMEOUT_MS
  );

  if (!result.success) {
    throw buildPinterestAdsApiError(
      result.error,
      "Pinterest Ads user_account fetch failed"
    );
  }

  const response = result.data;

  let data: RawUserAccountResponse;
  try {
    data = (await response.json()) as RawUserAccountResponse;
  } catch {
    throw new Error("Invalid Pinterest Ads user_account response: not JSON");
  }

  const accountType = normaliseAccountType(data.account_type);

  return {
    username: data.username ?? "",
    accountType,
    profileImage: data.profile_image,
    websiteUrl: data.website_url,
  };
}

function normaliseAccountType(raw: string | undefined): PinterestUserAccountType {
  if (raw === "BUSINESS" || raw === "PARTNER" || raw === "PINNER") {
    return raw;
  }
  // Unexpected enum value — default to PINNER so downstream code treats this
  // as the most restrictive class. Surfaces zero ad accounts → friendly
  // "no Pinterest ad accounts" error.
  return "PINNER";
}

// ============================================================================
// Ad Accounts (with cursor pagination)
// ============================================================================

/**
 * Enumerates every Pinterest ad account this OAuth grant can access (owned +
 * Business-Access-shared). Paginates via the `bookmark` cursor.
 *
 * Returns ad accounts sorted ascending by numeric `id` so the primary is
 * deterministic across re-auths regardless of API ordering — same lesson
 * as MS Ads review #2 H1. Pinterest IDs are numeric strings of varying
 * lengths (e.g. `"549755819515"`); a lexicographic sort would order
 * `"9..."` before `"10..."` so we MUST do BigInt-aware numeric comparison.
 *
 * @param accessToken - Bearer token returned by the token exchange
 * @param opts - Optional overrides (mostly for tests)
 */
export async function fetchPinterestAdsAdAccounts(
  accessToken: string,
  opts?: { pageSize?: number; maxPages?: number }
): Promise<PinterestAdAccountsResult> {
  const headers = buildPinterestHeaders(accessToken);
  const pageSize = opts?.pageSize ?? 100;
  const maxPages = opts?.maxPages ?? PINTEREST_AD_ACCOUNTS_MAX_PAGES;

  const accounts: PinterestAdAccount[] = [];
  /**
   * Defensive dedupe by id (Story 31.13 AC #4, ADR-0004 §Anti-Patterns 4):
   * a bookmark page-boundary echo (item appearing on two consecutive pages
   * during concurrent account churn) or a looping cursor must not inflate
   * the catalog with duplicates. First occurrence wins; the post-loop
   * numeric sort re-establishes deterministic ordering. The DB UNIQUE index
   * on `oauth_grant_resources` is the backstop, not the first line.
   * Composes with the `maxPages` cap (Pre-mortem C) — do not remove either.
   */
  const seenIds = new Set<string>();
  let bookmark: string | undefined;
  let pages = 0;
  let partialDiscovery = false;

  while (pages < maxPages) {
    pages += 1;

    const url = new URL(`${PINTEREST_API_BASE}/ad_accounts`);
    url.searchParams.set("page_size", String(pageSize));
    if (bookmark) {
      url.searchParams.set("bookmark", bookmark);
    }

    const result = await safeFetch(
      url.toString(),
      { method: "GET", headers },
      API_TIMEOUT_MS
    );

    if (!result.success) {
      throw buildPinterestAdsApiError(
        result.error,
        "Pinterest Ads ad_accounts fetch failed"
      );
    }

    const response = result.data;

    let data: RawAdAccountsResponse;
    try {
      data = (await response.json()) as RawAdAccountsResponse;
    } catch {
      throw new Error("Invalid Pinterest Ads ad_accounts response: not JSON");
    }

    const pageItems = data.items ?? [];
    for (const raw of pageItems) {
      const id = raw.id != null ? String(raw.id) : "";
      if (!id) continue;
      if (seenIds.has(id)) continue;
      seenIds.add(id);
      // String-coercion guard: only keep string elements; OMIT the property
      // entirely when the field is absent/malformed (Story 31.13 AC #6).
      const permissions = Array.isArray(raw.permissions)
        ? raw.permissions.filter((p): p is string => typeof p === "string")
        : undefined;
      accounts.push({
        id,
        name: raw.name ?? "",
        currency: raw.currency ?? "",
        country: raw.country ?? "",
        owner: {
          id: raw.owner?.id != null ? String(raw.owner.id) : "",
          username: raw.owner?.username ?? "",
        },
        agencyId: raw.agency_id != null ? String(raw.agency_id) : undefined,
        ...(permissions !== undefined ? { permissions } : {}),
      });
    }

    if (!data.bookmark) {
      // No more pages.
      break;
    }
    bookmark = data.bookmark;

    if (pages >= maxPages) {
      // Hit the cap with more potentially available — flag it.
      partialDiscovery = true;
      break;
    }
  }

  // Sort ascending by numeric id for deterministic primary selection across
  // re-auths. BigInt-aware so 12-digit numeric strings sort numerically not
  // lexicographically.
  accounts.sort(comparePinterestAccountIds);

  return { accounts, partialDiscovery };
}

/**
 * BigInt-aware ascending comparator for Pinterest numeric-string ad-account IDs.
 *
 * Pinterest IDs are numeric strings of varying lengths (e.g. `"549755819515"`).
 * `localeCompare` would order `"9..."` before `"10..."` lexicographically;
 * `Number()` would lose precision past 2^53. `BigInt` is exact across the full
 * range so primary selection stays stable across re-auths regardless of the
 * order Pinterest's API returns them in.
 */
function comparePinterestAccountIds(
  a: PinterestAdAccount,
  b: PinterestAdAccount
): number {
  // Defensive fallback if a non-numeric id ever surfaces — unlikely but
  // BigInt() throws on non-numeric strings, and we don't want sort to crash.
  if (!/^\d+$/.test(a.id) || !/^\d+$/.test(b.id)) {
    return a.id.localeCompare(b.id);
  }
  const bigA = BigInt(a.id);
  const bigB = BigInt(b.id);
  if (bigA < bigB) return -1;
  if (bigA > bigB) return 1;
  return 0;
}

// ============================================================================
// Internal: build a typed PinterestAdsApiCallError from a safeFetch error
// ============================================================================
//
// The adapter relies on `PinterestAdsApiCallError.type` so it can route 401 →
// token_expired without string-matching localised provider messages. This
// helper centralises the parsing logic shared by both REST calls.

function buildPinterestAdsApiError(
  safeFetchError: {
    message: string;
    providerMessage?: string;
    providerStatus?: number;
  },
  prefix: string
): PinterestAdsApiCallError {
  let parsedBody: unknown = {};
  if (safeFetchError.providerMessage) {
    try {
      parsedBody = JSON.parse(safeFetchError.providerMessage);
    } catch {
      // not JSON; fall back to status-only mapping
    }
  }
  const parsed = parsePinterestAdsApiError(
    parsedBody,
    safeFetchError.providerStatus
  );
  // Always produce a typed error — even when parsing yielded nothing
  // recognisable we synthesise an "unknown" type so the call site can rely on
  // `instanceof PinterestAdsApiCallError`.
  return new PinterestAdsApiCallError(
    parsed ?? {
      type: "unknown",
      message: sanitizeErrorMessage(safeFetchError.message),
      status: safeFetchError.providerStatus ?? null,
      retryable: false,
    },
    prefix
  );
}
